import React, { useState } from "react";
import PropTypes from "kf-proptypes/widgets";

import { DownArrow } from "../../icons/index.jsx";
import { BaseDropdown, BASE_DROPDOWN_POSITION } from "../dropdown/index.jsx";
import { Spinner } from "../../feedback/spinner/index.jsx";

import "antd/es/dropdown/style/css";
import styles from "./button.css";

import UpgradeIcon from "./upgrade.svg";

export const BUTTON_TYPES = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  TERTIARY: "tertiary",
  TERTIARY_ERROR: "tertiary_error",
  ERROR: "error",
  ACTION: "action",
  PLAIN: "plain",
  LINK: "link",
  UPGRADE: "upgrade",
  AI: "ai",
  DOTTED_PRIMARY: "dotted_primary",
  DOTTED_SECONDARY: "dotted_secondary"
};

export const BUTTON_SIZES = {
  SMALL: "small",
  SMALL_DEPRECATED: "smallDeprecated",
  BASE: "base",
  MEDIUM: "medium",
  LARGE: "large"
};

export const MENU_BUTTON_TRIGGER = {
  CLICK: "click",
  HOVER: "hover",
  CONTEXT_MENU: "contextMenu"
};

export const BUTTON_SHAPE = {
  NORMAL: "normal",
  ROUND: "round"
};

/* eslint-disable */
function textClassForSize(size) {
  switch (size) {
    case BUTTON_SIZES.SMALL:
      return "small";
    case BUTTON_SIZES.SMALL_DEPRECATED:
    case BUTTON_SIZES.MEDIUM:
      return "smallplus";
    case BUTTON_SIZES.BASE:
      return "base";
    case BUTTON_SIZES.LARGE:
      return "h6";
    default:
      return "base";
  }
}

function loaderSize(size) {
  switch (size) {
    case BUTTON_SIZES.SMALL:
    case BUTTON_SIZES.SMALL_DEPRECATED:
    case BUTTON_SIZES.MEDIUM:
      return 16;
    case BUTTON_SIZES.BASE:
      return 24;
    case BUTTON_SIZES.LARGE:
      return 28;
    default:
      return 24;
  }
}

export function Button(props) {
  const {
    children,
    className,
    onClick,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onContextMenu,
    size = BUTTON_SIZES.BASE,
    type = BUTTON_TYPES.PRIMARY,
    isLoading = false,
    disabled,
    leftIcon,
    rightIcon,
    shape,
    ...restProps
  } = props;
  return (
    <button
      className={`${styles.button} ${className || ""} ${styles[type]} ${
        styles[size]
      } ${disabled ? styles.disabled : ""} ${leftIcon ? styles.leftIcon : ""} ${
        rightIcon ? styles.rightIcon : ""
      } ${isLoading ? styles.loading : ""} ${styles[shape]}`}
      onClick={onClick}
      disabled={disabled}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onContextMenu={onContextMenu}
      data-component="button"
      {...restProps}
    >
      {isLoading && (
        <Spinner
          className={styles.loader}
          size={loaderSize(size)}
          spinnerDelay={0}
        />
      )}
      {!isLoading && <div className={styles.iconContainer}>{leftIcon}</div>}
      {!isLoading && <span>{children}</span>}
      {!isLoading && <div className={styles.iconContainer}>{rightIcon}</div>}
    </button>
  );
}

Button.propTypes = {
  /**
   * Children to pass inside the button
   */
  children: PropTypes.node.isRequired,
  /**
   * To override the styles of button
   */
  className: PropTypes.string,
  /**
   * Trigger when user click the button
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Trigger when user focus the button
   */
  onFocus: PropTypes.func,
  /**
   * Trigger when user move enter the button
   */
  onMouseEnter: PropTypes.func,
  /**
   * Trigger when user leave the button
   */
  onMouseLeave: PropTypes.func,

  onContextMenu: PropTypes.func,
  /**
   * Size of the button
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * Different variation of button can be handled by this prop
   */
  type: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Button Loading state will be there
   */
  isLoading: PropTypes.bool,
  /**
   * Button Disabled State
   */
  disabled: PropTypes.bool,
  /**
   * Left Icon to be displayed in button
   */
  leftIcon: PropTypes.node,
  /**
   * Right Icon to be displayed in button
   */
  rightIcon: PropTypes.node,
  /**
   * Shape of the button
   */
  shape: PropTypes.oneOf(Object.values(BUTTON_SHAPE))
};

export function MenuButton({
  children,
  className,
  size = BUTTON_SIZES.BASE,
  onClick,
  type = BUTTON_TYPES.PRIMARY,
  isLoading = false,
  disabled,
  menu,
  position = BASE_DROPDOWN_POSITION.BOTTOM_RIGHT,
  menuTrigger = [MENU_BUTTON_TRIGGER.CLICK],
  ...restProps
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <BaseDropdown
      trigger={menuTrigger}
      position={position}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      listRenderer={({ closePopup }) => {
        return (
          <React.Fragment>
            <div
              onClick={() => {
                closePopup();
                setIsOpen(false);
              }}
            >
              {menu}
            </div>
          </React.Fragment>
        );
      }}
      targetRenderer={() => {
        return (
          <Button
            className={`${className} ${styles.menubutton} ${
              isOpen && styles.upArrow
            }`}
            size={size}
            type={type}
            onClick={onClick}
            disabled={disabled}
            isLoading={isLoading}
            rightIcon={
              <DownArrow
                width={
                  size === BUTTON_SIZES.SMALL_DEPRECATED ? "0.8rem" : "1.2rem"
                }
                height={
                  size === BUTTON_SIZES.SMALL_DEPRECATED ? "0.8rem" : "1.2rem"
                }
              />
            }
            {...restProps}
          >
            {children}
          </Button>
        );
      }}
    />
  );
}

MenuButton.propTypes = {
  /**
   * Children to pass inside the menu Button
   */
  children: PropTypes.node.isRequired,
  /**
   * To override the styles of menu button
   */
  className: PropTypes.string,
  /**
   * Trigger when user click the menubutton
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Size of the button
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * Different variation of menu button can be handled by this prop
   */
  type: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Menu button Loading state will be there
   */
  isLoading: PropTypes.bool,
  /**
   * Menu button Disabled State
   */
  disabled: PropTypes.bool,
  /**
   * Menu content can be determined based on this prop
   */
  menu: PropTypes.node,
  /**
   * Button Position can be changed
   */
  position: PropTypes.oneOf(Object.values(BASE_DROPDOWN_POSITION)),
  menuTrigger: PropTypes.oneOf(Object.values(MENU_BUTTON_TRIGGER))
};

export function SplitButton({
  onClick,
  children,
  type = BUTTON_TYPES.PRIMARY,
  size = BUTTON_SIZES.BASE,
  isLoading,
  menu,
  menuTrigger = [MENU_BUTTON_TRIGGER.CLICK],
  className,
  disabled,
  shape = BUTTON_SHAPE.NORMAL,
  rightIcon,
  onRightIconClick,
  ...restProps
}) {
  return (
    <div
      className={`${styles.splitButton} ${styles[`split${type}`]} ${
        disabled ? styles.disabled : ""
      }  ${styles[shape]} ${className}`}
      data-component="splitbutton"
    >
      <Button
        className={styles.actionButton}
        onClick={onClick}
        type={type}
        size={size}
        isLoading={isLoading}
        disabled={disabled}
        {...restProps}
      >
        {children}
      </Button>
      <div className={styles.splitButtonDivider} />
      {rightIcon ? (
        <IconButton
          type={type}
          size={size}
          className={`${className} ${styles.iconButton} ${
            isLoading && styles.loadingDisabled
          }`}
          icon={rightIcon}
          disabled={disabled}
          onClick={onRightIconClick}
        />
      ) : (
        <BaseDropdown
          trigger={menuTrigger}
          position={BASE_DROPDOWN_POSITION.BOTTOM_RIGHT}
          listRenderer={() => {
            return !isLoading && <React.Fragment>{menu}</React.Fragment>;
          }}
          targetRenderer={() => {
            return (
              <IconButton
                type={type}
                size={size}
                className={`${className} ${styles.iconButton} ${
                  isLoading && styles.loadingDisabled
                }`}
                icon={<DownArrow />}
                disabled={disabled}
              />
            );
          }}
        />
      )}
    </div>
  );
}

SplitButton.propTypes = {
  /**
   * Children to pass inside the split button
   */
  children: PropTypes.node.isRequired,
  /**
   * To override the styles of split button
   */
  className: PropTypes.string,
  /**
   * Trigger when user click the split button
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Different variation of split button can be handled by this prop
   */
  type: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Size of the split button
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * Split button Loading state will be there
   */
  isLoading: PropTypes.bool,
  /**
   * Menu content can be changed
   */
  menu: PropTypes.node,
  /**
   * Menu Trigger types onclick, hover
   */
  menuTrigger: PropTypes.oneOf(Object.values(MENU_BUTTON_TRIGGER)),
  /**
   * Split button Disabled State
   */
  disabled: PropTypes.bool,
  /**
   * Shape of the split button
   */
  shape: PropTypes.oneOf(Object.values(BUTTON_SHAPE)),
  /**
   * Right Icon to be displayed in button
   */
  rightIcon: PropTypes.node,
  /**
   * When user click the rightIcon this function will trigger
   */
  onRightIconClick: PropTypes.func
};

export function IconButton(props) {
  const {
    icon,
    onClick,
    disabled,
    className,
    size = BUTTON_SIZES.BASE,
    type = BUTTON_TYPES.PRIMARY,
    isLoading = false,
    ...restProps
  } = props;
  return (
    <Button
      className={`${className} ${styles.iconButton}`}
      disabled={disabled}
      size={size}
      type={type}
      onClick={onClick}
      isLoading={isLoading}
      {...restProps}
    >
      {!isLoading && icon}
    </Button>
  );
}

IconButton.propTypes = {
  /**
   * Icon to be rendered
   */
  icon: PropTypes.node,
  /**
   * Icon Button Disabled State
   */
  disabled: PropTypes.bool,
  /**
   * Trigger when user click the icon button
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Size of the icon button
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * Different variation of icon button can be handled by this prop
   */
  type: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * To override the styles of icon button
   */
  className: PropTypes.string,
  /**
   * Icon button Loading state will be there
   */
  isLoading: PropTypes.bool
};

export function GroupButton(props) {
  let childrens = React.Children.map(props.children, (button) => {
    const { active, disabled, onClick, children, size, type, ...restProps } =
      (button && button.props) || {};
    let additionalClassName = "";
    if (active) {
      additionalClassName = styles.groupButtonActive;
    }
    return (
      children && (
        <Button
          // type={type}
          size={size}
          onClick={onClick}
          disabled={disabled}
          className={`${additionalClassName} base600 ${styles.button} ${styles[type]}`}
          {...restProps}
        >
          {children}
        </Button>
      )
    );
  });
  return <div className={styles.groupButtons}>{childrens}</div>;
}

export function UpgradeButton(props) {
  return (
    <Button
      type={BUTTON_TYPES.UPGRADE}
      size={BUTTON_SIZES.SMALL_DEPRECATED}
      leftIcon={<UpgradeIcon />}
      {...props}
    />
  );
}

export const BUTTON_STYLES = styles;
